import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import queryString from "query-string";
import { 
  BellIcon,
  RotateCcwIcon,
  KeyIcon,
  UserIcon,
  SettingsIcon
} from "lucide-react";
import { IntegrationsService } from "../../services/IntegrationsService";
import { usePermissions } from "../../PermissionsProvider";
import DevelopersIntegrations from "./DevelopersIntegrations";
import NotificationSettings from "./NotificationSettings";
import DevelopersAPIKeys from "./DevelopersAPIKeys";
import Users from "./Users";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import OverflowTabs, { TabItem } from "../ui/overflow-tabs";
import { cn } from "../../lib/utils";

const DevelopersBase: React.FC = () => {
  const { apiKey } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string>("users");
  const [slackCode, setSlackCode] = useState<string | null>(null);

  // Map hash to tab keys
  const tabKeys: any = {
    users: "users",
    overview: "overview",
    apikeys: "apikeys",
    webhooks: "webhooks",
    integrations: "integrations",
    logs: "logs",
    notifications: "notifications",
  };

  useEffect(() => {
    const parsedQuery = queryString.parse(location.search);
    const hash = location.hash.replace("#", "");

    setActiveTab(tabKeys[hash] || "users");

    // Extract Slack OAuth code if present
    if (parsedQuery.code) {
      setSlackCode(parsedQuery.code as string);
    }
  }, [location]);

  useEffect(() => {
    if (slackCode) {
      IntegrationsService.handleSlackAuthSuccess(
        slackCode,
        null,
        apiKey
      ).subscribe(
        (res: any) => {
          console.log(`Success authenticating with Slack! ${res}`);
        },
        (error: any) => {
          console.log(`Error authenticating with Slack! ${error}`);
        }
      );
    }
  }, [slackCode]);

  // Tab configuration
  const tabs: TabItem[] = [
    {
      key: "users",
      label: "Users",
      icon: <UserIcon className="w-4 h-4" />
    },
    {
      key: "apikeys",
      label: "API Keys",
      icon: <KeyIcon className="w-4 h-4" />
    },
    {
      key: "integrations",
      label: "Integrations",
      icon: <RotateCcwIcon className="w-4 h-4" />
    },
    {
      key: "notifications",
      label: "Notifications",
      icon: <BellIcon className="w-4 h-4" />
    }
  ];

  // Tab content mapping
  const getTabContent = (tabKey: string) => {
    switch (tabKey) {
      case "users":
        return <Users />;
      case "apikeys":
        return <DevelopersAPIKeys />;
      case "integrations":
        return <DevelopersIntegrations />;
      case "notifications":
        return <NotificationSettings />;
      default:
        return <Users />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button 
                    onClick={() => navigate("/")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Home
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">Settings</span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="space-y-1">
              <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                Settings
              </h1>
              <p className="text-lg text-muted-foreground font-gc-reg">
                Manage your API keys, integrations, and notification preferences
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Card className="border-border">
          <CardContent className="p-0">
            {/* Tab Navigation */}
            <div className="border-b border-border">
              <OverflowTabs
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={setActiveTab}
              />
            </div>

            {/* Tab Content */}
            <div className="p-8">
              {getTabContent(activeTab)}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DevelopersBase;
